import * as XLSX from "xlsx";  
import { getApplicationsByGroups } from "../api/application";  
import { getProvince } from "../api/competition";  
  
const adjustColumnWidths = (data) => {  
  const wscols = [];  
  const columns = Object.keys(data[0]);  
  columns.forEach((column) => {  
    let maxLength = column.length;  
    data.forEach((row) => {  
      const value = row[column] ? row[column].toString() : "";  
      maxLength = Math.max(maxLength, value.length);  
    });  
    wscols.push({ wch: maxLength + 2 });  
  });  
  return wscols;  
};  
  
function buildApplicationsMap(applications) {  
  // Map: { [groupId]: { [eventName]: {styles: [...], instruments: [...] }}}  
  const byGroup = {};  
  applications.forEach(app => {  
    if (!app.group || !app.event) return;  
    const gId = app.group._id?.toString?.() ?? app.group;  
    byGroup[gId] = byGroup[gId] || {};  
    byGroup[gId][app.event.name] = byGroup[gId][app.event.name] || {styles: [], instruments: []};  
    byGroup[gId][app.event.name].styles.push(app.event.style);  
    if (app.event.name.toLowerCase().includes("instrumental")) {  
      byGroup[gId][app.event.name].instruments = byGroup[gId][app.event.name].instruments || [];  
      byGroup[gId][app.event.name].instruments.push(app.instrument || "");  
    }  
  });  
  return byGroup;  
}  
  
function getAllEventNames(applications) {  
  const set = new Set();  
  applications.forEach(app => {  
    if (app.event && app.event.name) set.add(app.event.name);  
  });  
  return Array.from(set);  
}  
  
const formatDate = (isoDate) => {  
  const d = new Date(isoDate);  
  const day = d.getDate();  
  const month = d.toLocaleString('default', { month: 'long' });  
  const year = d.getFullYear();  
  return `${day} ${month} ${year}`;  
};  
  
function buildAuditionDateString(province) {  
  if (!province || typeof province !== 'object') return "";  
  const name = province.province || "";  
  const date = province.date ? formatDate(province.date) : "";  
  const location = province.location || "";  
  return [name, date, location].filter(Boolean).join(" - ");  
}  
  
export const exportGroups = async (groups, provinceId) => {  
  const groupIds = groups.map(g => g._id.toString());  
  const applications = await getApplicationsByGroups(groupIds);  
  
  const appsMap = buildApplicationsMap(applications);  
  
  const eventNames = getAllEventNames(applications);  
  
  // Paid status map for groups  
  const paidStatusMap = {};  
  groupIds.forEach(gId => {  
    const apps = applications.filter(app => {  
      const id = app.group?._id?.toString?.() ?? app.group;  
      return id === gId;  
    });  
    paidStatusMap[gId] = apps.length && apps.every(app => app.paid)  
      ? "Paid"  
      : "Not Paid";  
  });  
  
  // Group basic columns  
  let baseColumns = [  
    "Audition Date",  
    "Group Number",  
    "Group Name",  
    "Group Size",  
    "Group Type",  
    "Group Age",  
    "Members",  
    "Member IDs",  
    "Teacher Name",  
    "Teacher Number",  
    "Teacher Email",  
    "Provincial Director Name"  
  ];  
  
  // Event columns (add instruments after every "instrumental" event)  
  let eventColumnSpecs = [];  
  eventNames.forEach(eventName => {  
    eventColumnSpecs.push({ name: eventName });  
    if (eventName.toLowerCase().includes("instrumental")) {  
      eventColumnSpecs.push({ name: `${eventName} - Instrument` });  
    }  
  });  
  
  // Compose data rows  
  const data = groups.map(group => {  
    const {  
      group_number,  
      group_name,  
      group_size,  
      group_type,  
      group_age,  
      group: membersArr,  
      teacher_name,  
      teacher_number,  
      teacher_email,  
      provincial_director_name,  
    } = group;  
  
    const memberNames = Array.isArray(membersArr) ? membersArr.map(m => m.name).join("; ") : "";  
    const memberIDs = Array.isArray(membersArr) ? membersArr.map(m => m.id_number).join("; ") : "";  
  
    const row = {  
      "Audition Date": buildAuditionDateString(group.province),  
      "Group Number": group_number,  
      "Group Name": group_name,  
      "Group Size": group_size,  
      "Group Type": group_type,  
      "Group Age": group_age,  
      "Members": memberNames,  
      "Member IDs": memberIDs,  
      "Teacher Name": teacher_name,  
      "Teacher Number": teacher_number,  
      "Teacher Email": teacher_email,  
      "Provincial Director Name": provincial_director_name,  
    };  
  
    // Applications for this group  
    const groupApplications = appsMap[group._id.toString()] || {};  
    eventColumnSpecs.forEach(spec => {  
      if (spec.name.endsWith(" - Instrument")) {  
        const eventName = spec.name.replace(" - Instrument", "");  
        const apps = groupApplications[eventName];  
        row[spec.name] = apps && apps.instruments && apps.instruments.length  
          ? apps.instruments.filter(Boolean).join(', ')  
          : "";  
      } else {  
        const apps = groupApplications[spec.name];  
        row[spec.name] = apps && apps.styles.length  
          ? apps.styles.join(', ')  
          : "";  
      }  
    });  
  
    // Add "Paid" as the last column  
    row["Paid"] = paidStatusMap[group._id.toString()] || "Not Paid";  
  
    return row;  
  });  
  
  // Compose column order: base columns, event columns, Paid  
  const allColumns = [  
    ...baseColumns,  
    ...eventColumnSpecs.map(x => x.name),  
    "Paid"  
  ];  
  
  // Order data to match column order  
  const orderedData = data.map(row =>  
    Object.fromEntries(allColumns.map(col => [col, row[col] || ""]))  
  );  
  
  const worksheet = XLSX.utils.json_to_sheet(orderedData);  
  worksheet["!cols"] = adjustColumnWidths(orderedData);  
  const workbook = XLSX.utils.book_new();  
  XLSX.utils.book_append_sheet(workbook, worksheet, 'GroupsSheet');  
  XLSX.writeFile(workbook, "Groups.xlsx");  
};  